import React from 'react';
import { Badge, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { EMPTY, formatUsd, isIrtCredit } from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import ModelShareChips from './ModelShareChips';
import {
  Code, CountCell, MarginBadge, ParsehCell, PriceSourceBadge, TomanCell, UsdCell, useServiceLabel,
} from './shared';

/**
 * The heart of the page: one row per service, the columns ARE the chain, in
 * reading order — عامل → مدل‌ها → اجرا → توکن → دلار → تومان → پارسه → حاشیه
 * (design §12.5).
 *
 * Null discipline is the whole table: a NULL cost is «—» with the unpriced
 * chip explaining why, a NULL margin is a dash rather than a zero-percent chip,
 * and a conditional service shows its charged rate beside the run count so a
 * thin margin reads as the truth rather than a flattering one (§7.4).
 */
export default function ServicesTable({ rows, view, onSort, onOpen, empty, creditCurrency }) {
  const { t, formatNumber, formatPercent } = useTranslation();
  const label = useServiceLabel();
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const tokens = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 0 }));

  const columns = [
    {
      key: 'service',
      header: t('aiPricing.services.columns.service'),
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground" title={label(r)}>{label(r)}</div>
          <div className="truncate text-xs text-muted-foreground"><Code>{r.serviceKey}</Code></div>
        </div>
      ),
    },
    {
      key: 'models',
      header: t('aiPricing.services.columns.models'),
      width: '16rem',
      render: (r) => <ModelShareChips models={r.models || []} />,
    },
    {
      key: 'invocations',
      header: t('aiPricing.services.columns.invocations'),
      sortable: true,
      align: 'end',
      width: '8rem',
      render: (r) => {
        const rate = num(r.chargedRate);
        return (
          <span className="inline-flex flex-col items-end gap-0.5">
            <CountCell value={r.invocations} />
            {rate != null && rate < 1 ? (
              <Badge variant="outline" className="tabular-nums" title={t('aiPricing.services.chargedRate', { pct: formatPercent(rate * 100) })}>
                {t('aiPricing.services.chargedRate', { pct: formatPercent(rate * 100) })}
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'tokens',
      header: t('aiPricing.services.columns.tokens'),
      align: 'end',
      width: '10rem',
      render: (r) => (
        <span className="tabular-nums" title={t('aiPricing.services.cachedHint', { value: tokens(r.avgCachedTokens) })}>
          {tokens(r.avgInputTokens)} / {tokens(r.avgOutputTokens)}
        </span>
      ),
    },
    {
      key: 'cost',
      header: t('aiPricing.services.columns.costUsd'),
      sortable: true,
      align: 'end',
      width: '9rem',
      render: (r) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <UsdCell value={r.meanCostUsd} title={t('aiPricing.services.p50Hint', { value: usd(r.p50CostUsd) })} />
          {r.hasUnpricedModel && num(r.pricedShare) != null && num(r.pricedShare) < 1 ? (
            <Badge variant="destructive" title={t('aiPricing.services.unpricedRow', { share: formatPercent(num(r.pricedShare) * 100) })}>
              {formatPercent(num(r.pricedShare) * 100)}
            </Badge>
          ) : null}
        </span>
      ),
    },
    // A toman cost exists only where a credit is priced in toman (PM-0211); a
    // USD deployment has no FX and therefore no such column.
    isIrtCredit(creditCurrency) ? {
      key: 'costToman',
      header: t('aiPricing.services.columns.costToman'),
      align: 'end',
      width: '8rem',
      render: (r) => <TomanCell value={r.meanCostToman} />,
    } : null,
    {
      key: 'price',
      header: t('aiPricing.services.columns.price'),
      sortable: true,
      align: 'end',
      width: '11rem',
      render: (r) => (
        <span className="inline-flex flex-wrap items-center justify-end gap-1">
          <ParsehCell value={r.effectiveParseh} suffix={r.unit === 'minute' ? t('aiPricing.services.perMinute') : ''} className="font-semibold" />
          <PriceSourceBadge source={r.priceSource} />
        </span>
      ),
    },
    {
      key: 'margin',
      header: t('aiPricing.services.columns.margin'),
      sortable: true,
      align: 'end',
      width: '7rem',
      render: (r) => <MarginBadge value={r.marginPct} data-testid={`ai-services-row-${r.serviceKey}-margin`} />,
    },
    {
      key: 'declared',
      header: t('aiPricing.services.columns.declared'),
      align: 'end',
      width: '8rem',
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-1">
          <ParsehCell value={r.declaredInAppsParseh} />
          {r.appsMismatch ? <Badge variant="warning">{t('aiPricing.services.mismatchBadge')}</Badge> : null}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.services.tableAria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.serviceKey}
      onRowActivate={onOpen}
      rowProps={(r) => ({ 'data-testid': `ai-services-row-${r.serviceKey}`, 'aria-label': t('aiPricing.services.open', { label: label(r) }) })}
      sort={{ key: view.sort || 'cost', dir: view.dir || 'desc' }}
      onSort={onSort}
      empty={empty}
      data-testid="ai-services-table"
    />
  );
}
